import { platformApiFetch } from '@/services/platformApi/client';
import type {
  AdminListingsAction,
  ApproveListingRequest,
  ApproveListingResponse,
  CreateAdminListingRequest,
  CreateAdminListingResponse,
  GetListingReviewPayloadResponse,
  GetModerationQueueResponse,
  RejectListingRequest,
  RejectListingResponse,
  UpdateAdminListingRequest,
  UpdateAdminListingResponse,
} from '@/types/api';
const invokeAdminListings = async <T>(action: AdminListingsAction): Promise<T> => {
  switch (action.action) {
    case 'list':
      return platformApiFetch<T>(`/v1/admin/listings?statusFilter=${action.payload.statusFilter ?? 'pending'}&limit=${action.payload.limit ?? 100}`);
    case 'reviewPayload':
      return platformApiFetch<T>(`/v1/admin/listings/${action.payload.listingId}/review-payload`);
    case 'create':
      return platformApiFetch<T>('/v1/admin/listings', { method: 'POST', body: JSON.stringify(action.payload) });
    case 'update':
      return platformApiFetch<T>(`/v1/admin/listings/${action.payload.listingId}`, { method: 'PUT', body: JSON.stringify(action.payload) });
    case 'approve':
      return platformApiFetch<T>(`/v1/admin/listings/${action.payload.listingId}/approve`, { method: 'POST', body: JSON.stringify(action.payload) });
    case 'reject':
      return platformApiFetch<T>(`/v1/admin/listings/${action.payload.listingId}/reject`, { method: 'POST', body: JSON.stringify(action.payload) });
  }
};

const buildRequestId = () => crypto.randomUUID();
const buildIdempotencyKey = (prefix: string) => `${prefix}:${crypto.randomUUID()}`;

export const getModerationQueue = async (statusFilter: 'all' | 'pending' | 'approved' | 'rejected' | 'unassigned') => {
  return invokeAdminListings<GetModerationQueueResponse>({
    action: 'list',
    payload: { statusFilter, limit: 100 },
    requestId: buildRequestId(),
  });
};

export const getListingReviewPayload = async (listingId: string) => {
  return invokeAdminListings<GetListingReviewPayloadResponse>({
    action: 'reviewPayload',
    payload: { listingId },
    requestId: buildRequestId(),
  });
};

export const createAdminListing = async (payload: CreateAdminListingRequest) => {
  return invokeAdminListings<CreateAdminListingResponse>({
    action: 'create',
    payload,
    requestId: buildRequestId(),
    idempotencyKey: buildIdempotencyKey('admin-listing-create'),
  });
};

export const updateAdminListing = async (payload: UpdateAdminListingRequest) => {
  return invokeAdminListings<UpdateAdminListingResponse>({
    action: 'update',
    payload,
    requestId: buildRequestId(),
    idempotencyKey: buildIdempotencyKey(`admin-listing-update:${payload.listingId}`),
  });
};

export const approveAdminListing = async (payload: ApproveListingRequest) => {
  return invokeAdminListings<ApproveListingResponse>({
    action: 'approve',
    payload,
    requestId: buildRequestId(),
    idempotencyKey: buildIdempotencyKey(`admin-listing-approve:${payload.listingId}`),
  });
};

export const rejectAdminListing = async (payload: RejectListingRequest) => {
  return invokeAdminListings<RejectListingResponse>({
    action: 'reject',
    payload,
    requestId: buildRequestId(),
    idempotencyKey: buildIdempotencyKey(`admin-listing-reject:${payload.listingId}`),
  });
};